import { useEffect } from 'react';

import { addOrUpdateFormField } from '../store/applicationform';
import { selectFieldStateByName } from '../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../store/applicationform/applicationFormTypes';
import { KycCountries } from '../types/KycTypes';
import { useAppDispatch, useAppSelector } from '.';
import useSettings from './useSettings';

/**
 * Pre-selects the applicant's own market as their citizenship (LIP-1679, generalized for LP-2863), so the customer
 * only has to touch the field when it is wrong. Guarded against the backend's own country list rather than a
 * hardcoded set of markets, so a market only gets the default once know-your-customer-service actually offers its
 * country as an option.
 *
 * Previously lived in KycCitizenshipGroup, which owned the whole citizenship group. That component assumed the v1
 * template's alternating COUNTRY_CODE_SELECTION/BUTTON layout and silently dropped every question that did not fit, so
 * the group is now rendered by KycGroup like any other and this is the one piece of behaviour worth keeping.
 *
 * Deliberately scoped by the caller to the citizenship group's country question: the new-account group also has a
 * COUNTRY_CODE_SELECTION ("angi hvilket land"), and defaulting that one would answer it wrongly on the customer's
 * behalf, since it is only shown once they have said the money comes from somewhere else.
 *
 * @param fieldName the field to default
 * @param enabled whether this question is the one that should carry the default
 * @param countryOptions the citizenship question's own country list, to check the default against
 */
const useDefaultCitizenship = (fieldName: string, enabled: boolean, countryOptions?: KycCountries[]) => {
    const dispatch = useAppDispatch();
    const { countryCode } = useSettings();
    const value = useAppSelector(selectFieldStateByName)(fieldName)?.value;
    const hasValue = Array.isArray(value) ? value.length > 0 : !!value;

    useEffect(() => {
        if (!enabled || hasValue) {
            return;
        }

        const defaultCountryId = countryCode.toLowerCase();
        const hasMatchingCountry = countryOptions?.some((country) => country.id === defaultCountryId);

        if (!hasMatchingCountry) {
            return;
        }

        dispatch(
            addOrUpdateFormField({
                fieldName,
                value: [defaultCountryId],
                hasError: false,
            } as IFieldState),
        );
    }, [dispatch, fieldName, enabled, countryCode, hasValue, countryOptions]);
};

export default useDefaultCitizenship;
